import { getApp } from 'utils/sub-domains'
import { Rule, Tier } from 'types/progressiveDiscountTypes'

export function buildOriginCheckout(): string {
  return `representante-${getApp()}`
}

export function formatBRL(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    currencyDisplay: 'symbol',
  }).format(value)
}

// Piso da faixa: trigger_type='amount' exibe em R$, 'quantity' (default) exibe em unidades
export function formatTierFloor(tier: Tier, triggerType?: string): string {
  if (triggerType === 'amount') {
    return formatBRL(tier.min_quantity)
  }
  return `${tier.open_ended ? `${tier.min_quantity}+` : tier.min_quantity} un`
}

// Benefício sobre o item — usado apenas quando benefit_target='both' (item + frete)
export function itemBenefitLabel(tier: Tier, discountType: string | undefined, unitPrice: number | undefined): string {
  if (discountType === 'value' && unitPrice !== undefined) {
    return `${formatBRL(unitPrice - tier.unit_final_price)} OFF nos itens`
  }
  return `${tier.discount_percent}% OFF nos itens`
}

// Benefício sobre o frete — usado apenas quando benefit_target='both' (item + frete)
export function shippingBenefitLabel(tier: Tier): string | null {
  if (tier.shipping_value === undefined) return null
  if (tier.shipping_discount_type === 'value') {
    return `${formatBRL(tier.shipping_value)} OFF no frete`
  }
  return `${tier.shipping_value}% OFF no frete`
}

// Item puro (benefit_target='item') com discount_type='value': 'R$ Y OFF' (percent puro nao usa este helper)
export function pureItemValueLabel(tier: Tier, unitPrice: number | undefined): string {
  const diff = unitPrice !== undefined ? unitPrice - tier.unit_final_price : 0
  return `${formatBRL(diff)} OFF`
}

// Frete puro (benefit_target='shipping') com discount_type='value': 'R$ Y OFF no frete'.
// tier.value ainda nao e exposto pelo backend em todo lugar — enquanto nao chega, cai no percent (fallback).
export function pureShippingValueLabel(tier: Tier): string {
  if (tier.value === undefined) return `${tier.discount_percent}%`
  return `${formatBRL(tier.value)} OFF no frete`
}

export interface NextTierHint {
  missing: number
  missingLabel: string
  isAmountTrigger: boolean
  nextPercent: number
  itemLabel?: string
  freightLabel?: string | null
  pureValueLabel?: string
}

export function nextTierHint(tiers: Tier[], currentQty: number, rule?: Rule, unitPrice?: number): NextTierHint | null {
  if (!tiers || tiers.length === 0) return null

  const sorted = [...tiers].sort((a, b) => a.min_quantity - b.min_quantity)
  const next = sorted.find(t => t.min_quantity > currentQty)

  if (!next) return null

  const missing = next.min_quantity - currentQty
  const isAmountTrigger = rule?.trigger_type === 'amount'

  const hint: NextTierHint = {
    missing,
    missingLabel: isAmountTrigger ? formatBRL(missing) : `${missing}`,
    isAmountTrigger,
    nextPercent: next.discount_percent,
  }

  if (rule?.benefit_target === 'both') {
    hint.itemLabel = itemBenefitLabel(next, rule.discount_type, unitPrice)
    hint.freightLabel = shippingBenefitLabel(next)
  } else if (rule?.benefit_target === 'shipping') {
    // discount_percent e sempre 0 em frete puro — sem isso o hint cairia no fallback "0% OFF".
    hint.pureValueLabel = shippingBenefitLabel(next) ?? `${next.discount_percent}%`
  } else if (rule?.discount_type === 'value') {
    hint.pureValueLabel = pureItemValueLabel(next, unitPrice)
  }

  return hint
}
